/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { lodash } from '@ba-ui-toolkit/utils';
import { BiContext } from './ContextProvider';

const { forOwn, assign } = lodash;

function addStringProperty(dest, source, name, key) {
	const segments = name.split('.');
	let value = source;
	for (let i = 0; i < segments.length; i += 1) {
		value = value[segments[i]];
	}
	dest[key != null ? key : segments.pop()] = value;
}

function addObjectProperty(dest, source, obj) {
	forOwn(obj, (value, key) => {
		addStringProperty(dest, source, value, key);
	});
}

function addFunctionProperty(dest, source, func, ownProps) {
	const result = func(source, ownProps);
	if (result && typeof result === 'object') {
		assign(dest, result);
	}
}

function addProperty(dest, source, arg, ownProps) {
	switch (typeof arg) {
		case 'string':
			addStringProperty(dest, source, arg);
			break;
		case 'object':
			addObjectProperty(dest, source, arg);
			break;
		case 'function':
			addFunctionProperty(dest, source, arg, ownProps);
			break;
		default:
	}
}

export default function withContext(...args) {
	return function(ComponentClass) {
		// eslint-disable-line func-names
		class WithContext extends Component {
			static propTypes = {
				contextForwardRef: PropTypes.any,
			};

			static contextTypes = {
				biContext: PropTypes.object,
			};

			render() {
				return (
					<BiContext.Consumer>
						{biContext => {
							const { contextForwardRef, ...others } = this.props;
							const newProps = {};
							if (biContext) {
								if (args == null || args.length === 0) {
									newProps.context = biContext;
								} else {
									args.forEach(arg =>
										addProperty(newProps, biContext, arg, this.props)
									);
								}
							}

							return (
								<ComponentClass {...others} {...newProps} ref={contextForwardRef} />
							);
						}}
					</BiContext.Consumer>
				);
			}
		}

		WithContext.WrappedComponent = ComponentClass;

		return React.forwardRef((props, ref) => <WithContext {...props} contextForwardRef={ref} />);
	};
}
